import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContractVariableSourceEnum } from '@clickersphere/core/enums/ContractVariableSourceEnum';
import { IContractTemplateParagraph } from '@clickersphere/core/services/ContractTemplateService';
import useToast from '../../../../../app/hooks/useToast';
import {
  IAnchorSelection,
  ICreateAnchorPayload,
} from '../useTemplateModalController';
import { IEditableContractVariable } from '../VariableMappingEditor';

export interface IParagraphSegment {
  id: string;
  text: string;
  varKey?: string;
  varLabel?: string;
}

export interface IReviewAnchor {
  varKey: string;
  varLabel: string;
  paragraphIndex: number;
  matchText: string;
}

interface IUseDocumentVariableEditorControllerParams {
  paragraphs: IContractTemplateParagraph[];
  variables: IEditableContractVariable[];
  onCreateAnchor: (payload: ICreateAnchorPayload) => void;
  onRemoveAnchorByMatch: (
    varKey: string,
    paragraphIndex: number,
    matchText: string,
  ) => void;
}

interface IAnchorRange {
  start: number;
  end: number;
  varKey: string;
  varLabel: string;
}

const PREVIEW_VALUE_KEYS = {
  [ContractVariableSourceEnum.LEAD]:
    'contract.templates.editor.previewValues.LEAD',
  [ContractVariableSourceEnum.LEAD_LEGAL]:
    'contract.templates.editor.previewValues.LEAD_LEGAL',
  [ContractVariableSourceEnum.CONTRACTOR]:
    'contract.templates.editor.previewValues.CONTRACTOR',
  [ContractVariableSourceEnum.SERVICE]:
    'contract.templates.editor.previewValues.SERVICE',
  [ContractVariableSourceEnum.PAYMENT]:
    'contract.templates.editor.previewValues.PAYMENT',
  [ContractVariableSourceEnum.ISSUANCE]:
    'contract.templates.editor.previewValues.ISSUANCE',
} as const;

type PreviewSource = keyof typeof PREVIEW_VALUE_KEYS;

function buildParagraphSegments(
  text: string,
  variables: IEditableContractVariable[],
  paragraphIndex: number,
): IParagraphSegment[] {
  const ranges: IAnchorRange[] = [];

  variables.forEach((variable) => {
    variable.anchors.forEach((anchor) => {
      if (anchor.paragraphIndex !== paragraphIndex) return;
      if (!anchor.matchText) return;

      let fromIndex = 0;
      let foundIndex = text.indexOf(anchor.matchText, fromIndex);
      while (foundIndex !== -1) {
        ranges.push({
          start: foundIndex,
          end: foundIndex + anchor.matchText.length,
          varKey: variable.key,
          varLabel: variable.label || variable.key,
        });
        fromIndex = foundIndex + anchor.matchText.length;
        foundIndex = text.indexOf(anchor.matchText, fromIndex);
      }
    });
  });

  if (ranges.length === 0) return [{ id: `${paragraphIndex}-0`, text }];

  ranges.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    return b.end - a.end;
  });

  const selectedRanges: IAnchorRange[] = [];
  let lastEnd = -1;
  ranges.forEach((range) => {
    if (range.start >= lastEnd) {
      selectedRanges.push(range);
      lastEnd = range.end;
    }
  });

  const segments: IParagraphSegment[] = [];
  let cursor = 0;
  selectedRanges.forEach((range) => {
    if (range.start > cursor) {
      segments.push({
        id: `${paragraphIndex}-${cursor}`,
        text: text.slice(cursor, range.start),
      });
    }
    segments.push({
      id: `${paragraphIndex}-${range.start}`,
      text: text.slice(range.start, range.end),
      varKey: range.varKey,
      varLabel: range.varLabel,
    });
    cursor = range.end;
  });

  if (cursor < text.length) {
    segments.push({
      id: `${paragraphIndex}-${cursor}`,
      text: text.slice(cursor),
    });
  }

  return segments;
}

export function useDocumentVariableEditorController({
  paragraphs,
  variables,
  onCreateAnchor,
  onRemoveAnchorByMatch,
}: IUseDocumentVariableEditorControllerParams) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [selection, setSelection] = useState<IAnchorSelection | null>(null);
  const [reviewAnchor, setReviewAnchor] = useState<IReviewAnchor | null>(null);
  const [isPreviewFilled, setIsPreviewFilled] = useState(false);

  const exampleValueByKey = useMemo(() => {
    const map = new Map<string, string>();

    variables.forEach((variable) => {
      if (variable.source === ContractVariableSourceEnum.CUSTOM) {
        map.set(variable.key, variable.label || variable.key);
        return;
      }

      const translationKey =
        PREVIEW_VALUE_KEYS[variable.source as PreviewSource];
      map.set(
        variable.key,
        translationKey ? t(translationKey) : variable.label || variable.key,
      );
    });

    return map;
  }, [variables, t]);

  const renderedParagraphs = useMemo(
    () =>
      paragraphs.map((paragraph) => {
        const segments = buildParagraphSegments(
          paragraph.text,
          variables,
          paragraph.index,
        );

        if (!isPreviewFilled) {
          return { index: paragraph.index, segments };
        }

        return {
          index: paragraph.index,
          segments: segments.map((segment) =>
            segment.varKey
              ? {
                  ...segment,
                  text: exampleValueByKey.get(segment.varKey) ?? segment.text,
                }
              : segment,
          ),
        };
      }),
    [paragraphs, variables, isPreviewFilled, exampleValueByKey],
  );

  function handleSelectionEnd() {
    if (isPreviewFilled) return;

    const domSelection = window.getSelection();
    if (!domSelection || domSelection.isCollapsed) return;

    const matchText = domSelection.toString().trim();
    if (!matchText) return;

    if (matchText.length < 2 || !/[\p{L}\p{N}]/u.test(matchText)) {
      toast(t('contract.templates.editor.selectionTooShort'), 'warning');
      return;
    }

    const anchorNode = domSelection.anchorNode;
    const focusNode = domSelection.focusNode;
    if (!anchorNode || !focusNode) return;

    const anchorElement =
      anchorNode.nodeType === Node.ELEMENT_NODE
        ? (anchorNode as Element)
        : anchorNode.parentElement;
    const focusElement =
      focusNode.nodeType === Node.ELEMENT_NODE
        ? (focusNode as Element)
        : focusNode.parentElement;

    const anchorParagraph = anchorElement?.closest('[data-paragraph-index]');
    const focusParagraph = focusElement?.closest('[data-paragraph-index]');

    if (
      !anchorParagraph ||
      !focusParagraph ||
      anchorParagraph !== focusParagraph
    ) {
      toast(t('contract.templates.editor.selectionInvalid'), 'warning');
      return;
    }

    const rawIndex = anchorParagraph.getAttribute('data-paragraph-index');
    if (rawIndex === null) return;

    const paragraphIndex = Number(rawIndex);
    if (Number.isNaN(paragraphIndex)) return;

    setSelection({ paragraphIndex, matchText });
  }

  function handleConfirmAnchor(payload: ICreateAnchorPayload) {
    onCreateAnchor(payload);
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }

  function handleCloseSelection() {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }

  function handleMarkClick(
    varKey: string,
    varLabel: string,
    paragraphIndex: number,
    matchText: string,
  ) {
    if (isPreviewFilled) return;
    setReviewAnchor({ varKey, varLabel, paragraphIndex, matchText });
  }

  function handleCloseReview() {
    setReviewAnchor(null);
  }

  function handleRemoveReviewAnchor() {
    if (!reviewAnchor) return;
    onRemoveAnchorByMatch(
      reviewAnchor.varKey,
      reviewAnchor.paragraphIndex,
      reviewAnchor.matchText,
    );
    setReviewAnchor(null);
  }

  function handleChangeReviewVariable(payload: ICreateAnchorPayload) {
    if (!reviewAnchor) return;
    onRemoveAnchorByMatch(
      reviewAnchor.varKey,
      reviewAnchor.paragraphIndex,
      reviewAnchor.matchText,
    );
    onCreateAnchor(payload);
    setReviewAnchor(null);
  }

  function handleTogglePreview(checked: boolean) {
    setIsPreviewFilled(checked);
    if (checked) {
      setSelection(null);
      setReviewAnchor(null);
      window.getSelection()?.removeAllRanges();
    }
  }

  return {
    t,
    selection,
    reviewAnchor,
    isPreviewFilled,
    paragraphs,
    variables,
    renderedParagraphs,
    handleSelectionEnd,
    handleConfirmAnchor,
    handleCloseSelection,
    handleMarkClick,
    handleCloseReview,
    handleRemoveReviewAnchor,
    handleChangeReviewVariable,
    handleTogglePreview,
  };
}
